import type { Renderer, RenderOptions, WebGPURenderer } from "pixi.js";

interface Mirror {
  canvas: HTMLCanvasElement;
  context: GPUCanvasContext;
  onError: () => void;
}

/** Copies the completed frame on the same device, without captureStream or CPU pixels. */
export class GpuCanvasMirror {
  private readonly mirrors = new Set<Mirror>();
  private destroyed = false;

  static create(renderer: Renderer): GpuCanvasMirror | null {
    if (renderer?.name !== "webgpu") return null;
    return new GpuCanvasMirror(renderer as WebGPURenderer);
  }

  private constructor(private readonly renderer: WebGPURenderer) {
    renderer.runners.postrender.add(this);
    void renderer.gpu.device.lost.then(() => {
      if (!this.destroyed) this.fail();
    });
  }

  attach(canvas: HTMLCanvasElement, onError: () => void): () => void {
    if (this.destroyed) throw new Error("Preview renderer is no longer available");
    const context = canvas.getContext("webgpu");
    if (!context) throw new Error("WebGPU preview is unavailable");
    canvas.width = this.renderer.canvas.width;
    canvas.height = this.renderer.canvas.height;
    try {
      context.configure({
        device: this.renderer.gpu.device,
        format: "bgra8unorm",
        alphaMode: this.renderer.background.alpha === 1 ? "opaque" : "premultiplied",
        usage: GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
      });
    } catch (error) {
      context.unconfigure();
      throw error;
    }
    const mirror = { canvas, context, onError };
    this.mirrors.add(mirror);
    return () => {
      if (this.mirrors.delete(mirror)) context.unconfigure();
    };
  }

  postrender(options: RenderOptions): void {
    if (this.destroyed || !this.mirrors.size) return;
    // Thumbnail snapshots render other targets; never publish those as the live frame.
    if (options.target !== this.renderer.view.renderTarget) return;
    try {
      const { width, height } = this.renderer.canvas;
      const source = this.renderer.canvas.getContext("webgpu") as GPUCanvasContext;
      const texture = source.getCurrentTexture();
      const encoder = this.renderer.gpu.device.createCommandEncoder();
      for (const { canvas, context } of this.mirrors) {
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
        encoder.copyTextureToTexture(
          { texture },
          { texture: context.getCurrentTexture() },
          [width, height],
        );
      }
      this.renderer.gpu.device.queue.submit([encoder.finish()]);
    } catch (error) {
      console.warn("[GpuCanvasMirror] falling back to video preview", error);
      this.fail();
    }
  }

  private fail(): void {
    const mirrors = [...this.mirrors];
    this.destroy();
    mirrors.forEach(mirror => mirror.onError());
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.renderer.runners.postrender.remove(this);
    this.mirrors.forEach(mirror => mirror.context.unconfigure());
    this.mirrors.clear();
  }
}
